import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { DietaPersonaleCounter, DieteFiltri, ApiResponse } from '../../../types/diete';
import DettaglioDieteModal from './DettaglioDieteModal';
import Accordion from '../../controls/Accordion';
import PaginationControls from '../../controls/PaginationControls';
import SortIcon from '../../controls/SortIcon';
import useAccordion from '../../../hooks/useAccordion';
import usePagination from '../../../hooks/usePagination';
import useSort from '../../../hooks/useSort';
import './AlimentiPersonali.css';


const DietePersonali: React.FC = () => {
  const navigate = useNavigate();
  const [risultati, setRisultati] = useState<DietaPersonaleCounter[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Hook per ordinamento
  const { sortedData, sortConfig, handleSort } = useSort(risultati, {
    initialField: 'numero',
    initialDirection: 'desc'
  });
  
  // Hook per paginazione
  const {
    paginatedData,
    paginationInfo,
    goToPage,
    goToFirstPage,
    goToPreviousPage, 
    goToNextPage,
    goToLastPage,
    changeItemsPerPage,
    resetToFirstPage
  } = usePagination(sortedData, { initialItemsPerPage: 10 });
  
  // Filtri
  const [filtri, setFiltri] = useState<DieteFiltri>({
    dataDa: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 giorni fa
    dataA: new Date().toISOString().split('T')[0], // oggi
  });

  // Stato per accordion
  const filtersAccordion = useAccordion(true);
  const statsAccordion = useAccordion(true);
  
  // Stato per la modale
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<DietaPersonaleCounter | null>(null);

  const cercaDiete = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const payload = {
        dataDa: filtri.dataDa,
        dataA: filtri.dataA
      };

      const response = await api.post<ApiResponse<DietaPersonaleCounter[]>>('/DietePersonali/conteggio', payload);
      
      if (response.data.success) {
        // Aggiungi il campo nomeCompleto per compatibilità con il sorting
        const risultatiConNomeCompleto = response.data.data.map(item => ({
          ...item,
          nomeCompleto: `${item.nome} ${item.cognome}`.trim()
        }));
        setRisultati(risultatiConNomeCompleto);
        resetToFirstPage(); // Reset alla prima pagina per nuova ricerca
      } else {
        setError(response.data.message || 'Errore nella ricerca');
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Errore durante la ricerca');
      console.error('Errore ricerca diete:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFiltroChange = (campo: keyof DieteFiltri, valore: string) => {
    setFiltri(prev => ({
      ...prev,
      [campo]: valore
    }));
  };

  const handleDieteClick = (user: DietaPersonaleCounter) => {
    setSelectedUser(user);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedUser(null);
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      const payload = {
        dataDa: filtri.dataDa,
        dataA: filtri.dataA
      };

      const response = await api.post('/DietePersonali/export', payload, {
        responseType: 'blob'
      });

      // Crea un link per il download
      const url = window.URL.createObjectURL(response.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ConteggioDietePersonali_${filtri.dataDa}_${filtri.dataA}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (error: any) {
      setError('Errore durante l\'esportazione Excel');
      console.error('Errore export Excel:', error);
    } finally {
      setLoading(false);
    }
  };


  // Calcoli statistiche
  const totaleDiete = risultati.reduce((sum, item) => sum + item.numero, 0);
  const mediaDiete = risultati.length > 0 ? (totaleDiete / risultati.length).toFixed(1) : '0';


  return (
    <div className="alimenti-personali-container">
      <div className="page-header">
        <div className="header-top">
          <button 
            className="btn-back" 
            onClick={() => navigate('/')}
            title="Torna alla Dashboard"
          >
            ← Dashboard
          </button>
        </div>
        <div className="header-content-main">
          <div className="header-text">
            <h1>Diete Personali</h1>
            <p className="page-description">
              Visualizza e analizza le diete personali create dagli utenti del sistema
            </p>
          </div>
          
          <div className="header-actions">
            <button 
              className="btn-header-action" 
              onClick={() => navigate('/diete-personali')}
              disabled={true}
              title="Sei già nella vista Conteggio"
            >
              📊 Conteggio
            </button>
          </div>
        </div>
      </div>

      {/* Sezione Filtri */}
      <div className="filtri-section">
        <Accordion 
          title="Filtri di Ricerca"
          isOpen={filtersAccordion.isOpen}
          onToggle={filtersAccordion.toggle}
        >
          <div className="filtri-grid">
            <div className="filtro-item">
              <label htmlFor="dataDa">Data Da:</label>
              <input
                type="date"
                id="dataDa"
                value={filtri.dataDa}
                onChange={(e) => handleFiltroChange('dataDa', e.target.value)}
              />
            </div>

            <div className="filtro-item">
              <label htmlFor="dataA">Data A:</label>
              <input
                type="date"
                id="dataA"
                value={filtri.dataA}
                onChange={(e) => handleFiltroChange('dataA', e.target.value)}
              />
            </div>
          </div>

          <div className="filtri-actions">
            <button 
              className="btn-export" 
              onClick={handleExport}
              disabled={loading}
              title="Esporta i risultati in formato Excel"
            >
              📊 Esporta Excel
            </button>
            
            <button 
              className="btn-primary" 
              onClick={cercaDiete}
              disabled={loading}
            >
              {loading ? 'Ricerca in corso...' : 'Cerca'}
            </button>
          </div>
        </Accordion>
      </div>

      {/* Statistiche Rapide */}
      {risultati.length > 0 && (
        <div className="statistiche-section">
          <Accordion 
            title="Statistiche"
            isOpen={statsAccordion.isOpen}
            onToggle={statsAccordion.toggle}
          >
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-value">{risultati.length}</div>
                <div className="stat-label">Utenti con Diete</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{totaleDiete}</div>
                <div className="stat-label">Totale Diete</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{mediaDiete}</div>
                <div className="stat-label">Media per Utente</div>
              </div>
            </div>
          </Accordion>
        </div>
      )}

      {/* Messaggio di errore */}
      {error && (
        <div className="error-message">
          <p>⚠️ {error}</p>
        </div>
      )}

      {/* Tabella Risultati */}
      <div className="risultati-section">
        <h2>Risultati</h2>
        
        {loading && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Caricamento dati...</p>
          </div>
        )}

        {!loading && risultati.length === 0 && !error && (
          <div className="no-results">
            <p>Nessun risultato trovato per i filtri selezionati.</p>
            <p>Prova a modificare i criteri di ricerca.</p>
          </div>
        )}

        {!loading && risultati.length > 0 && (
          <>
            <PaginationControls
              paginationInfo={paginationInfo}
              onGoToPage={goToPage}
              onGoToFirstPage={goToFirstPage}
              onGoToPreviousPage={goToPreviousPage}
              onGoToNextPage={goToNextPage}
              onGoToLastPage={goToLastPage}
              onChangeItemsPerPage={changeItemsPerPage}
              showBottomControls={false}
            />

            <div className="table-container">
              <table className="risultati-table">
                <thead>
                  <tr>
                    <th 
                      className="sortable"
                      onClick={() => handleSort('numero')}
                      title="Clicca per ordinare"
                    >
                      Numero Diete <SortIcon field="numero" sortConfig={sortConfig} />
                    </th>
                    <th 
                      className="sortable"
                      onClick={() => handleSort('username')}
                      title="Clicca per ordinare"
                    >
                      Username <SortIcon field="username" sortConfig={sortConfig} />
                    </th>
                    <th 
                      className="sortable"
                      onClick={() => handleSort('nomeCompleto')}
                      title="Clicca per ordinare"
                    >
                      Cognome <SortIcon field="nomeCompleto" sortConfig={sortConfig} />
                    </th>
                    <th>Nome</th>
                    <th 
                      className="sortable"
                      onClick={() => handleSort('email')}
                      title="Clicca per ordinare"
                    >
                      Email <SortIcon field="email" sortConfig={sortConfig} />
                    </th>
                    <th>IdUser</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((item, index) => (
                    <tr key={`${item.idUser}-${index}`}>
                      <td className="numero-diete">
                        <button 
                          className="badge-numero clickable"
                          onClick={() => handleDieteClick(item)}
                          title={`Visualizza le ${item.numero} diete di ${item.nomeCompleto}`}
                        >
                          {item.numero}
                        </button>
                      </td>
                      <td className="username">
                        {item.username}
                      </td>
                      <td className="cognome">
                        {item.cognome}
                      </td>
                      <td className="nome">
                        {item.nome}
                      </td>
                      <td className="email">
                        {item.email}
                      </td>
                      <td className="id-user">
                        {item.idUser}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <PaginationControls
              paginationInfo={paginationInfo}
              onGoToPage={goToPage}
              onGoToFirstPage={goToFirstPage}
              onGoToPreviousPage={goToPreviousPage}
              onGoToNextPage={goToNextPage}
              onGoToLastPage={goToLastPage}
              onChangeItemsPerPage={changeItemsPerPage}
              showTopControls={false}
            />
          </>
        )}
      </div>

      {/* Modale per il dettaglio delle diete */}
      {selectedUser && (
        <DettaglioDieteModal
          isOpen={modalOpen}
          onClose={handleModalClose}
          idUser={selectedUser.idUser}
          nomeUtente={selectedUser.nomeCompleto}
          numeroDiete={selectedUser.numero}
          filtri={filtri}
        />
      )}
    </div>
  );
};

export default DietePersonali;